/**
 * ConfirmActionCard — renders a pending write-action confirmation UI inside
 * the chat thread. Shown when the AI assistant emits a `confirm_required` event.
 *
 * States:
 *   pending   — Confirm + Cancel buttons active; optional busy spinner while
 *               the confirm/cancel request is in flight.
 *   confirmed — Both buttons disabled; shows a success line (✓ resultMessage).
 *   cancelled — Both buttons disabled; shows "Cancelled".
 *   error     — Both buttons disabled; shows an error Alert.
 */

import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';

// ---------------------------------------------------------------------------
// Types (exported so ChatMessage.tsx can re-export the shape)
// ---------------------------------------------------------------------------

export interface ConfirmPayload {
  actionId: string;
  toolName: string;
  title: string;
  summary: string;
  fields: { label: string; value: string }[];
  status: 'pending' | 'confirmed' | 'cancelled' | 'error';
  resultMessage?: string;
}

interface ConfirmActionCardProps {
  confirm: ConfirmPayload;
  onConfirm?: () => void;
  onCancel?: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ConfirmActionCard: React.FC<ConfirmActionCardProps> = ({
  confirm,
  onConfirm,
  onCancel,
}) => {
  const { status, title, summary, fields, resultMessage } = confirm;
  const isPending = status === 'pending';

  // Local busy state: true while a confirm/cancel request is in flight.
  // We flip it on button click and rely on the parent updating `status`
  // (via message state) to eventually disable the buttons permanently.
  const [busy, setBusy] = useState(false);

  const handleConfirm = async () => {
    if (!isPending || busy || !onConfirm) return;
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    if (!isPending || busy || !onCancel) return;
    setBusy(true);
    try {
      await onCancel();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        borderColor: '#CD853F',
        borderLeftWidth: 4,
        borderLeftColor: '#8B4513',
        borderLeftStyle: 'solid',
        boxShadow: '0 1px 6px rgba(139,69,19,0.15)',
        maxWidth: 420,
        bgcolor: '#FAF9F6',
      }}
    >
      <CardContent sx={{ pb: '12px !important' }}>
        {/* Title */}
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, color: '#8B4513', mb: 0.5 }}
        >
          {title}
        </Typography>

        {/* Summary */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {summary}
        </Typography>

        {/* Field rows */}
        {fields.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <Divider sx={{ mb: 1, borderColor: 'divider' }} />
            {fields.map((f) => (
              <Box
                key={f.label}
                sx={{
                  display: 'flex',
                  gap: 1,
                  mb: 0.5,
                  flexWrap: 'wrap',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, color: 'text.secondary', minWidth: 100, flexShrink: 0 }}
                >
                  {f.label}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.primary' }}>
                  {f.value}
                </Typography>
              </Box>
            ))}
            <Divider sx={{ mt: 1, borderColor: 'divider' }} />
          </Box>
        )}

        {/* Outcome display when not pending */}
        {status === 'confirmed' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
            <CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: 18 }} />
            <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 500 }}>
              {resultMessage ?? 'Action confirmed'}
            </Typography>
          </Box>
        )}

        {status === 'cancelled' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
            <CancelOutlinedIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
            <Typography variant="body2" color="text.secondary">
              Cancelled
            </Typography>
          </Box>
        )}

        {status === 'error' && (
          <Alert severity="error" sx={{ py: 0.5, mb: 1, borderRadius: 1.5 }}>
            {resultMessage ?? 'Something went wrong. Please try again.'}
          </Alert>
        )}

        {/* Confirm / Cancel buttons */}
        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
          <Button
            variant="contained"
            size="small"
            disabled={!isPending || busy}
            onClick={handleConfirm}
            startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}
            sx={{
              bgcolor: '#8B4513',
              color: '#FAF9F6',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 1.5,
              px: 2,
              '&:hover': { bgcolor: '#7a3b10' },
              '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
            }}
          >
            Confirm
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={!isPending || busy}
            onClick={handleCancel}
            sx={{
              borderColor: '#CD853F',
              color: '#8B4513',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 1.5,
              px: 2,
              '&:hover': { borderColor: '#8B4513', bgcolor: 'rgba(139,69,19,0.05)' },
              '&.Mui-disabled': { borderColor: 'action.disabledBackground' },
            }}
          >
            Cancel
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ConfirmActionCard;
